import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { RootState } from 'reducers';
import { Globals } from 'utils';
import { HomeService } from 'services';
import { FallbackImage } from 'components';
import { WhiteImage } from 'assets/img';
import { ContactCallIcon, ContactMessageIcon, TimeIcon } from 'assets/icons';
import '../../../scss/screens/affiliate/profile.scss';

const getInitialContactMethods = (contactMethodsData: any) => {
    let contactMethodsArr: number[] = [];
    try {
        if (typeof contactMethodsData === 'string') {
            if (contactMethodsData.startsWith('[')) {
                contactMethodsArr = JSON.parse(contactMethodsData).map(Number);
            } else {
                contactMethodsArr = [Number(contactMethodsData)];
            }
        } else if (Array.isArray(contactMethodsData)) {
            contactMethodsArr = contactMethodsData.map(Number);
        } else if (typeof contactMethodsData === 'number') {
            contactMethodsArr = [contactMethodsData];
        }
    } catch (error) {
        console.warn("Error parseando métodos de contacto en perfil:", error);
    }
    return {
        video: contactMethodsArr.includes(1),
        call: contactMethodsArr.includes(2),
        chat: contactMethodsArr.includes(3),
    };
};

const formatTimeAMPM = (timeStr?: string) => {
    if (!timeStr) return '';
    if (timeStr.toLowerCase().includes('am') || timeStr.toLowerCase().includes('pm')) return timeStr;
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
        let hours = parseInt(parts[0], 10);
        const minutes = parts[1];
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;
        const strHours = hours < 10 ? `0${hours}` : hours;
        return `${strHours}:${minutes} ${ampm}`;
    }
    return timeStr;
};

// Helper para renderizar miniatura o icono de PDF
const renderDocumentThumbnail = (pathString?: string | null) => {
    if (!pathString) return null;

    const isPdf = pathString.toLowerCase().endsWith('.pdf');

    if (isPdf) {
        return (
            <div className="document-thumbnail-img flex-center" style={{ backgroundColor: '#E5E7EB', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <i className="fas fa-file-pdf" style={{ fontSize: '32px', color: '#9CA3AF' }}></i>
            </div>
        );
    }

    return (
        <FallbackImage
            src={Globals.fromPhotos(pathString)}
            placeholder={WhiteImage}
            className="document-thumbnail-img"
            alt="Documento"
        />
    );
};

const AffiliateProfile = () => {
    const nav = useHistory();
    const user = useSelector((state: RootState) => state.user);

    const [isLoading, setIsLoading] = useState(true);
    const [profileData, setProfileData] = useState<any>(null);
    const [selects, setSelects] = useState<any>(null);
    const [activeTab, setActiveTab] = useState<'datos' | 'documentos' | 'productos' | 'veterinarios'>('datos');
    const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

    const loadProfileData = useCallback(async () => {
        if (!user?.id) return;
        setIsLoading(true);
        try {
            const [profileRes, selectsRes]: any = await Promise.all([
                HomeService.affiliate.getProfile({ user_id: user.id }),
                HomeService.affiliate.getSelects({ user_id: user.id })
            ]);
            setProfileData(profileRes);
            setSelects(selectsRes || {});
        } catch (error) {
            console.error("Error al cargar el perfil:", error);
        } finally {
            setIsLoading(false);
        }
    }, [user?.id]);

    useEffect(() => {
        loadProfileData();
    }, [loadProfileData]);

    if (isLoading) {
        return (
            <main id="affiliate-profile">
                <div className="loading-profile-container">
                    <div className="spinner"></div>
                    <p>Cargando perfil...</p>
                </div>
            </main>
        );
    }

    if (!profileData) return null;

    const currentMethods = getInitialContactMethods(profileData.consultation?.contact_methods || 0);
    const expYears = profileData.professional_data?.years;
    const phoneCode = selects?.phonecodes?.find((p: any) => p.id === profileData.phonecode_id)?.name || '';
    const bankInfo = selects?.banks?.find((b: any) => b.id === profileData.bank_account?.bank_id)?.name || '';

    const renderDatosTab = () => (
        <div className="tab-pane fade-in">
            <div className="description-section">
                <p className="tab-section-title">Descripción</p>
                <p className={`description-text ${isDescriptionExpanded ? 'expanded' : ''}`}>
                    {profileData.description || 'Sin descripción detallada.'}
                </p>
                {profileData.description && profileData.description.length > 150 && (
                    <button className="btn-link" onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}>
                        {isDescriptionExpanded ? 'Ver menos' : 'Ver más'}
                    </button>
                )}
            </div>

            {profileData.languages && profileData.languages.length > 0 && (
                <div className="bordered-box">
                    <p className="box-title">Atención a consultas en el (los) siguiente(s) idioma(s)</p>
                    <p className="box-content-text">
                        {profileData.languages.map((l: any) => l.language_txt || l.name).join(' - ')}
                    </p>
                </div>
            )}

            {profileData.user_specialties && profileData.user_specialties.length > 0 && (
                <div className="bordered-box">
                    <p className="box-title">Especialidades</p>
                    <div className="pill-container">
                        {profileData.user_specialties.map((spec: any, idx: number) => (
                            <span key={idx} className="yellow-pill">{spec.specialty_txt || spec.name}</span>
                        ))}
                    </div>
                </div>
            )}

            {profileData.areas_specialization && profileData.areas_specialization.length > 0 && (
                <div className="checklist-section">
                    <p className="box-title">Áreas de Especialización</p>
                    <div className="checklist-grid">
                        {profileData.areas_specialization.map((area: any, idx: number) => (
                            <div key={idx} className="check-item">
                                <i className="fas fa-check-square"></i>
                                <span>{area.area_txt || area.name}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {profileData.affiliate_animals && profileData.affiliate_animals.length > 0 && (
                <div className="green-box">
                    <p className="box-title white-text">Especies de animales que atendemos</p>
                    <div className="pill-container">
                        {profileData.affiliate_animals.map((animal: any, idx: number) => (
                            <span key={idx} className="yellow-pill">{animal.type_txt || animal.name}</span>
                        ))}
                    </div>
                </div>
            )}

            {profileData.user_services && profileData.user_services.length > 0 && (
                <div className="bordered-box">
                    <p className="box-title">Servicios que ofrece</p>
                    <div className="pill-container">
                        {profileData.user_services.map((serv: any, idx: number) => (
                            <span key={idx} className="yellow-pill">{serv.type_txt || serv.name}</span>
                        ))}
                    </div>
                </div>
            )}

            {profileData.categories && profileData.categories.length > 0 && (
                <div className="categories-section">
                    <p className="box-title">Categorías</p>
                    <div className="categories-grid">
                        {profileData.categories.map((cat: any, idx: number) => {
                            const categoryInfo = selects?.categories?.find((masterCat: any) => masterCat.id === cat.category_id);
                            return (
                                <div key={idx} className="category-card">
                                    <FallbackImage
                                        src={categoryInfo?.image ? Globals.fromPhotos(categoryInfo.image) : undefined}
                                        placeholder={WhiteImage}
                                        alt="cat"
                                    />
                                    <span>{categoryInfo?.name || `Categoría #${cat.category_id}`}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );

    const renderDocumentosTab = () => {
        if (profileData.affiliate_type === 2 || profileData.affiliate_type === 3) {
            const docName = selects?.document_types?.find((d: any) => d.id === profileData.contact_person_doc_type_id)?.name || '';
            const contactPhoneCode = selects?.phonecodes?.find((p: any) => p.id === profileData.contact_person_phone_code_id)?.name || '';

            return (
                <div className="tab-pane fade-in">
                    <h3 className="profesional-data-title">Datos de Persona de Contacto</h3>

                    <div className="contact-box">
                        <span className={profileData.contact_person_name ? "text-dark" : "text-placeholder"}>
                            {profileData.contact_person_name || 'Nombre'}
                        </span>
                    </div>

                    <div className="contact-box-row">
                        <div className="contact-box-left">
                            <span className={docName ? "text-dark" : "text-placeholder"}>{docName || 'V'}</span>
                            <i className="fas fa-chevron-down" style={{ color: '#9CA3AF' }}></i>
                        </div>
                        <div className="contact-box-right">
                            <span className={profileData.contact_person_document ? "text-dark" : "text-placeholder"}>
                                {profileData.contact_person_document || 'Escribir'}
                            </span>
                        </div>
                    </div>

                    <div className="contact-box">
                        <span className={profileData.contact_person_phone ? "text-dark" : "text-placeholder"}>
                            {profileData.contact_person_phone ? `${contactPhoneCode} ${profileData.contact_person_phone}` : 'Teléfono'}
                        </span>
                    </div>

                    <div className="contact-box">
                        <span className={profileData.contact_person_email ? "text-dark" : "text-placeholder"}>
                            {profileData.contact_person_email || 'Correo'}
                        </span>
                    </div>
                </div>
            );
        }

        return (
            <div className="tab-pane fade-in">
                <div className="registration-header" style={{ textAlign: 'center', marginBottom: '25px' }}>
                    <p className="registration-number-title">Número de Registro en el Colegio de Médicos Veterinarios</p>
                    <p className="registration-number">
                        {profileData.professional_data?.medical_number || 'No registrado'}
                    </p>
                </div>

                <h3 className="profesional-data-title text-left">Datos profesionales</h3>

                <div className="documents-web-grid">
                    <div className="document-col">
                        <label className="document-section-label">Título Universitario</label>
                        {profileData.professional_data?.university_title ? (
                            <div className="document-card-orange">
                                {renderDocumentThumbnail(profileData.professional_data.university_title)}
                                <div className="document-card-info">
                                    <span className="document-card-text">Archivo adjunto</span>
                                    {profileData.professional_data.university_title.toLowerCase().endsWith('.pdf') && (
                                        <span className="document-pdf-label">PDF Document</span>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <p className="label-light">No tienes el título universitario registrado.</p>
                        )}
                    </div>

                    <div className="document-col">
                        <label className="document-section-label">Carnet Colegio Veterinario</label>
                        {profileData.professional_data?.medical_carnet ? (
                            <div className="document-card-orange">
                                {renderDocumentThumbnail(profileData.professional_data.medical_carnet)}
                                <div className="document-card-info">
                                    <span className="document-card-text">Archivo adjunto</span>
                                    {profileData.professional_data.medical_carnet.toLowerCase().endsWith('.pdf') && (
                                        <span className="document-pdf-label">PDF Document</span>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <p className="label-light">No tienes el carnet adjunto registrado.</p>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderProductosTab = () => (
        <div className="tab-pane fade-in">
            {profileData.products && profileData.products.length > 0 ? (
                <div className="products-web-grid">
                    {profileData.products.map((prod: any, idx: number) => (
                        <div key={idx} className="product-card-web">
                            <div className="product-image-container">
                                {prod.image ? (
                                    <FallbackImage
                                        src={Globals.fromPhotos(prod.image)}
                                        placeholder={WhiteImage}
                                        className="product-img"
                                        alt={prod.name}
                                    />
                                ) : (
                                    <i className="fas fa-box-open" style={{ fontSize: '40px', color: '#D1D5DB' }}></i>
                                )}
                            </div>
                            <p className="product-name">{prod.name}</p>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="empty-state">
                    <i className="fas fa-box-open empty-state-icon"></i>
                    <p className="label-light mt-10">No tienes productos registrados.</p>
                </div>
            )}
        </div>
    );

    const renderVeterinariosTab = () => (
        <div className="tab-pane fade-in">
            <h3 className="profesional-data-title">Nuestro Equipo Médico</h3>
            {profileData.affiliate_veterinarians && profileData.affiliate_veterinarians.length > 0 ? (
                <div className="veterinarios-list-web">
                    {profileData.affiliate_veterinarians.map((vet: any, idx: number) => (
                        <div key={idx} className="vet-card">
                            <div className="vet-info">
                                <p className="vet-name">{vet.name}</p>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="empty-state">
                    <i className="fas fa-users empty-state-icon"></i>
                    <p className="label-light mt-10">Aún no hay veterinarios registrados.</p>
                </div>
            )}
        </div>
    );

    return (
        <main id="affiliate-profile">
            <div className="profile-grid">

                <aside className="left-column">
                    <div className="profile-card">
                        <div className="banner-wrapper">
                            <FallbackImage
                                src={profileData.banner ? Globals.fromPhotos(profileData.banner) : undefined}
                                placeholder={WhiteImage}
                                className="banner-img"
                                placeholderClassName="banner-img"
                                alt="Banner"
                            />

                        </div>

                        <div className="avatar-wrapper">
                            <FallbackImage
                                src={profileData.photo ? Globals.fromPhotos(profileData.photo) : undefined}
                                placeholder={WhiteImage}
                                className="avatar-img"
                                placeholderClassName="avatar-img"
                                alt="Perfil"
                            />
                            <div className="user-intro">
                                <h2>{profileData.name}</h2>
                                <p>Experiencia de {expYears ? `${expYears} años` : 'No registrada'}</p>
                            </div>
                        </div>

                        <div className="basic-info">
                            <div className="info-item">
                                <i className="far fa-calendar"></i>
                                <span>Fecha de nacimiento {moment(profileData.birthdate).format('DD-MM-YYYY')}</span>
                            </div>
                            <div className="info-row-2">
                                <div className="info-item">
                                    <i className="fas fa-mobile-alt"></i>
                                    <span>{phoneCode} {profileData.phone}</span>
                                </div>
                                <div className="info-item">
                                    <i className="far fa-envelope"></i>
                                    <span>{profileData.email}</span>
                                </div>
                            </div>
                            <div className="info-item align-top">
                                <i className="fas fa-map-marker-alt"></i>
                                <span>{profileData.address || 'Sin dirección registrada'}</span>
                            </div>
                        </div>

                        {profileData.consultation && (
                            <>
                                <div className="schedule-info">
                                    <div className="schedule-header">
                                        <img src={TimeIcon} alt="clock" />
                                        <span>Horario {formatTimeAMPM(profileData.consultation.init_hour)} - {formatTimeAMPM(profileData.consultation.end_hour)}</span>
                                    </div>
                                    <div className="days-box">
                                        <span className="bold">Días</span>
                                        <span>{profileData.consultation.days_txt}</span>
                                    </div>
                                </div>

                                <div className="contact-methods-wrapper">
                                    <p className="title-sm">Método de contacto</p>
                                    <div className="methods-grid">
                                        <div className="method-pill">
                                            <img src={ContactCallIcon} alt="video" /> <span>Video llamada</span>
                                            <label className="toggle-switch"><input type="checkbox" checked={currentMethods.video} readOnly /><span className="slider"></span></label>
                                        </div>
                                        <div className="method-pill">
                                            <img src={ContactCallIcon} alt="call" /> <span>Llamada</span>
                                            <label className="toggle-switch"><input type="checkbox" checked={currentMethods.call} readOnly /><span className="slider"></span></label>
                                        </div>
                                        <div className="method-pill">
                                            <img src={ContactMessageIcon} alt="chat" /> <span>Chat</span>
                                            <label className="toggle-switch"><input type="checkbox" checked={currentMethods.chat} readOnly /><span className="slider"></span></label>
                                        </div>
                                    </div>
                                </div>
                                {/* 
                                <div className="extra-services-grid">
                                    <div className="extra-service-card">
                                        <i className="fas fa-check-circle green-check"></i>
                                        <span>Disponible para consultas a domicilio</span>
                                    </div>
                                    <div className="extra-service-card">
                                        <i className="fas fa-check-circle green-check"></i>
                                        <span>Presta servicio de emergencia 24 horas</span>
                                    </div>
                                </div> */}
                            </>
                        )}

                        {profileData.affiliate_type !== 3 && (
                            <div className="bank-data">
                                <p className="title-sm">Datos bancarios</p>
                                <p className="bank-text">{bankInfo} - {profileData.bank_account?.number}</p>
                                <div className="bank-details-row">
                                    <div>
                                        <span className="lbl">Teléfono</span>
                                        <span className="val">{profileData.bank_account?.phone || 'N/A'}</span>
                                    </div>
                                    <div>
                                        <span className="lbl">C.I.</span>
                                        <span className="val">{profileData.bank_account?.document || 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                        )}
                        {!!profileData.consultation?.teleconsultation_rate_id && (
                            <div className="cost-banner">
                                <div className="cost-left">
                                    <span>Costo por</span>
                                    <span>consulta</span>
                                </div>
                                <div className="cost-right">
                                    <span>${selects?.teleconsultationRates?.find((r: any) => r.id === profileData.consultation.teleconsultation_rate_id)?.amount || '0.00'}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </aside>

                <section className="right-column">
                    {/* <div className="action-buttons-row">
                        <button className="btn-black" onClick={() => nav.push('/profile/change-password')}>
                            <i className="fas fa-lock"></i> Cambiar contraseña
                        </button>
                        <button className="btn-yellow" onClick={() => nav.push('/profile/edit')}>
                            <i className="fas fa-pencil-alt"></i> Editar perfil
                        </button>
                    </div> */}

                    <div className="tabs-row">
                        <button className={`tab-btn ${activeTab === 'datos' ? 'active' : ''}`} onClick={() => setActiveTab('datos')}>Datos</button>
                        <button className={`tab-btn ${activeTab === 'documentos' ? 'active' : ''}`} onClick={() => setActiveTab('documentos')}>
                            {profileData?.affiliate_type === 1 ? 'Documentos' : 'Contacto'}
                        </button>
                        <button className={`tab-btn ${activeTab === 'productos' ? 'active' : ''}`} onClick={() => setActiveTab('productos')}>Producto</button>
                        {profileData?.affiliate_type === 2 && (
                            <button className={`tab-btn ${activeTab === 'veterinarios' ? 'active' : ''}`} onClick={() => setActiveTab('veterinarios')}>Veterinarios</button>
                        )}
                    </div>

                    <div className="tab-content-container">
                        {activeTab === 'datos' && renderDatosTab()}
                        {activeTab === 'documentos' && renderDocumentosTab()}
                        {activeTab === 'productos' && renderProductosTab()}
                        {activeTab === 'veterinarios' && renderVeterinariosTab()}
                    </div>
                </section>

            </div>
        </main>
    );
};

export default AffiliateProfile;